import { Stack } from '@fluentui/react';
import {
  Button,
  buttonStylesLink,
  IH2OTheme,
  IKeyValuePairEditorStyles,
  KeyValuePairEditor,
  useTheme,
} from '@h2oai/ui-kit';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useDebouncedCallback } from '../../../../utils/hooks';
import { MetadataFilterProps } from './types';

const metadataFilterStyles: (t: IH2OTheme, collapsed?: boolean) => Partial<IKeyValuePairEditorStyles> = (
  theme: IH2OTheme,
  collapsed = false
) => ({
  root: {
    height: 59,
    '.h2o-KeyValuePairEditor-validationErrorMessage': { display: 'none' },
  },
  header: {},
  valueColumn: {
    width: 120,
    fontWeight: 400,
    padding: '5px',
    marginRight: 10,
  },
  keyColumn: {
    width: 120,
    fontWeight: 400,
    padding: '5px',
    marginRight: 10,
  },
  buttonColumn: {
    fontWeight: 400,
    padding: '5px',
  },
  body: {
    padding: 0,
    position: 'relative',
    zIndex: 1,
    backgroundColor: theme.semanticColors?.contentBackground,
    border: `solid 1px ${theme.semanticColors?.inputBorder}`,
    borderRadius: 4,
    '.h2o-KeyValuePairEditor-row': { padding: 0 },
    '.h2o-KeyValuePairEditor-row:not(:first-child)': {
      display: collapsed ? 'none' : 'flex',
    },
    '.h2o-KeyValuePairEditor-keyColumn, .h2o-KeyValuePairEditor-valueColumn, .h2o-KeyValuePairEditor-buttonColumn': {
      padding: '8px 4px',
      margin: 0,
    },
  },
  emptyState: {
    margin: 0,
    padding: '7px 4px',
  },
  addButton: {
    top: 0,
    right: 0,
  },
});

const generateReadableValue = (value: Record<string, any>, inputLabel?: string): string => {
  if (!value || Object.keys(value).length === 0) return '';

  const pairs = Object.entries(value)
    .filter(([key, val]) => key && val !== undefined && val !== '')
    .map(([key, val]) => `${key}: ${val}`)
    .join(', ');

  if (!pairs) return '';

  return `${inputLabel || 'Metadata'}: ${pairs}`;
};

export const MetadataFilter = memo((props: MetadataFilterProps) => {
  const { onChange, value, inputLabel, disabled } = props;
  const [localValue, setLocalValue] = useState<Record<string, any>>(value ?? {});
  const [collapsed, setCollapsed] = useState(false);
  const keyValueEditorRef = useRef<any>(null);
  const hasInitialized = useRef(false);
  const theme = useTheme();

  const debouncedOnChange = useDebouncedCallback((inputData: Record<string, string>) => {
    const readableValue = generateReadableValue(inputData, inputLabel);
    onChange(inputData, readableValue);
  }, 400);

  const handleInputChange = useCallback(
    (inputData: Record<string, string>) => {
      setLocalValue(inputData);
      debouncedOnChange(inputData);
    },
    [debouncedOnChange]
  );

  useEffect(() => {
    setLocalValue(value || {});
  }, [value]);

  useEffect(() => {
    if (value && Object.keys(value).length > 0 && !hasInitialized.current) {
      const readableValue = generateReadableValue(value, inputLabel);
      if (readableValue) {
        onChange(value, readableValue);
        hasInitialized.current = true;
      }
    }
  }, [value, inputLabel, onChange]);

  const showToggle = Object.keys(localValue).length > 1;

  return (
    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="end">
      <KeyValuePairEditor
        styles={metadataFilterStyles(theme, collapsed)}
        ref={keyValueEditorRef}
        config={localValue}
        onUpdateConfig={handleInputChange}
        readOnly={disabled}
        onAddNewLine={() => setCollapsed(false)}
      />
      {showToggle && (
        <Button
          iconName={collapsed ? 'ChevronDown' : 'ChevronUp'}
          styles={buttonStylesLink}
          onClick={() => setCollapsed(!collapsed)}
          text={collapsed ? 'Expand overflow' : 'Collapse overflow'}
        />
      )}
    </Stack>
  );
});
